/** Reusable parent/child booking tree (over the main_booking relation).
 *
 * Renders the WHOLE tree the given nodes belong to (the backend already returns
 * root + all descendants), as a MUI `treeData` grid with a custom grouping cell
 * (guest name → user link + expand/collapse). The current booking's row is
 * highlighted. Returns null for a standalone booking (no parent, no children).
 * Every other row's booking is reachable through its `#id` (same-tab link +
 * the booking id actions) — there used to be no way to open a related booking.
 */
import { Box, IconButton, Link } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  DataGridPro,
  useGridApiContext,
  type GridColDef,
  type GridGroupingColDefOverride,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { BookingFamilyNode } from '@/api/bookings';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { bookingIdActions, registrationMethodActions, type BookingCtx } from './bookingLinks';
import { ContactLinks, READONLY_GRID_PROPS, READONLY_GRID_SX, TagsCell, Ts, UserLink } from './common';

type FamilyRow = BookingFamilyNode & { path: string[] };

/** Root→node id path for each node, cycle-guarded (a node whose parent is
 *  missing from the set is treated as a root). */
function buildRows(nodes: BookingFamilyNode[]): FamilyRow[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const cache = new Map<number, string[]>();
  const pathFor = (n: BookingFamilyNode, seen: Set<number>): string[] => {
    const cached = cache.get(n.id);
    if (cached) return cached;
    const parent = n.parent_id != null ? byId.get(n.parent_id) : undefined;
    const path = parent && !seen.has(parent.id)
      ? [...pathFor(parent, new Set(seen).add(n.id)), String(n.id)]
      : [String(n.id)];
    cache.set(n.id, path);
    return path;
  };
  return nodes.map((n) => ({ ...n, path: pathFor(n, new Set([n.id])) }));
}

function GuestGroupingCell({ ctx, ...params }: GridRenderCellParams<FamilyRow> & { ctx: BookingCtx }) {
  const apiRef = useGridApiContext();
  const { id, row, rowNode } = params;
  const isGroup = rowNode.type === 'group';
  const expanded = isGroup && rowNode.childrenExpanded;
  const depth = rowNode.depth ?? 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', pl: depth * 2, minWidth: 0 }}>
      {isGroup ? (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            apiRef.current.setRowChildrenExpansion(id, !expanded);
          }}
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
      ) : (
        <Box sx={{ width: 34, flexShrink: 0 }} />
      )}
      <UserLink user={row.user} ctx={ctx} />
    </Box>
  );
}

function buildColumns(ctx: BookingCtx): GridColDef<FamilyRow>[] {
  return [
    {
      field: 'booking',
      headerName: 'Booking',
      width: 110,
      sortable: false,
      renderCell: (p) => (
        <ValueActions actions={bookingIdActions(p.row.id, ctx)}>
          {p.row.is_current ? (
            <Box component="span" sx={{ fontWeight: 600 }}>#{p.row.id} (this)</Box>
          ) : (
            <Link component={RouterLink} to={`/bookings/${p.row.id}`} underline="hover">
              #{p.row.id}
            </Link>
          )}
        </ValueActions>
      ),
    },
    {
      field: 'contacts',
      headerName: 'Main contacts',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (p) => (
        <ContactLinks email={p.row.contacts.email} phone={p.row.contacts.phone} ctx={ctx} />
      ),
    },
    {
      field: 'registration_method',
      headerName: 'Registration',
      width: 150,
      renderCell: (p) =>
        p.row.registration_method ? (
          <ValueActions actions={registrationMethodActions(p.row.registration_method, ctx)}>
            {p.row.registration_method}
          </ValueActions>
        ) : (
          '—'
        ),
    },
    {
      field: 'registration_ts',
      headerName: 'Registered',
      width: 160,
      renderCell: (p) => <Ts value={p.row.registration_ts} />,
    },
    {
      field: 'last_login',
      headerName: 'Last login',
      width: 150,
      renderCell: (p) => <Ts value={p.row.last_login} relative />,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1.2,
      minWidth: 180,
      sortable: false,
      renderCell: (p) => <TagsCell tags={p.row.tags} ctx={ctx} />,
    },
  ];
}

export function BookingFamilyTree({ nodes, ctx }: { nodes: BookingFamilyNode[]; ctx: BookingCtx }) {
  const columns = useMemo(() => buildColumns(ctx), [ctx]);
  const groupingCol = useMemo<GridGroupingColDefOverride<FamilyRow>>(
    () => ({
      headerName: 'Guest',
      flex: 1,
      minWidth: 220,
      renderCell: (p) => <GuestGroupingCell {...(p as GridRenderCellParams<FamilyRow>)} ctx={ctx} />,
    }),
    [ctx],
  );
  if (nodes.length <= 1) return null;
  const rows = buildRows(nodes);
  return (
    <DataGridPro
      treeData
      rows={rows}
      columns={columns}
      getRowId={(r) => r.id}
      getTreeDataPath={(r) => r.path}
      groupingColDef={groupingCol}
      defaultGroupingExpansionDepth={-1}
      getRowClassName={(p) => (p.row.is_current ? 'current-booking' : '')}
      sx={[READONLY_GRID_SX, { '& .current-booking': { bgcolor: 'action.selected' } }]}
      {...READONLY_GRID_PROPS}
    />
  );
}
